import React, { useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { SelectValue } from 'antd/lib/select'
import { Button, ButtonType } from '@/components/atoms/button'
import { Option, Select } from '@/components/atoms/select'
import { DateField } from '@/components/molecules/date-field'
import { DateRangeField } from '@/components/molecules/date-range-field'
import {
  RESERVATION_TASK_HANDLING_STATUSES,
  RESERVATION_TASK_RESERVATION_STATUSES,
  ReservationTaskFilter,
  ReservationTaskStaff,
  reservationTaskHandlingStatusLabelKeys,
  reservationTaskReservationStatusLabelKeys,
} from '@/models/reservation/reservation-task'
import { HALF_HOUR_TIME_OPTIONS } from '@/utils/half-hour-time-options'
import {
  INITIAL_RESERVATION_TASK_QUERY,
  isDateTimeRangeValid,
  isReservationTaskHandlingStatus,
  isReservationTaskReservationStatus,
  joinDateTime,
  splitDateTime,
} from '@/utils/reservation-task'

const toNumbers = (value: SelectValue): number[] => (Array.isArray(value) ? value.map(item => Number(item)) : [])

type TimeSelectProps = {
  value: string
  disabled: boolean
  onChange: (time: string) => void
}

const TimeSelect: React.FC<TimeSelectProps> = ({ value, disabled, onChange }) => (
  <Select
    value={value || undefined}
    placeholder="--:--"
    allowClear
    disabled={disabled}
    onChange={selected => onChange(selected ? String(selected) : '')}
    customStyle={timeSelectStyle}
  >
    {HALF_HOUR_TIME_OPTIONS.map(time => (
      <Option key={time} value={time}>
        {time}
      </Option>
    ))}
  </Select>
)

type Props = {
  filter: ReservationTaskFilter
  staffOptions: ReservationTaskStaff[]
  onApply: (filter: ReservationTaskFilter) => void
}

export const ReservationTaskFilterPanel: React.FC<Props> = ({ filter, staffOptions, onApply }) => {
  const { t } = useTranslation()
  // 「適用」を押すまで一覧の条件を変えないよう、入力途中の値はパネルの中だけで持つ
  const [draft, setDraft] = useState<ReservationTaskFilter>(filter)
  const [hasRangeError, setHasRangeError] = useState(false)
  const updatedAtFrom = splitDateTime(draft.reservationUpdatedAtFrom)
  const updatedAtTo = splitDateTime(draft.reservationUpdatedAtTo)

  const changeDraft = (change: Partial<ReservationTaskFilter>) => {
    setHasRangeError(false)
    setDraft(current => ({ ...current, ...change }))
  }

  const apply = () => {
    if (!isDateTimeRangeValid(draft.reservationUpdatedAtFrom, draft.reservationUpdatedAtTo)) {
      setHasRangeError(true)
      return
    }
    onApply(draft)
  }

  return (
    <div css={panelStyle}>
      <div css={fieldRowStyle}>
        <div css={selectFieldStyle}>
          <div css={fieldTitleStyle}>{t('reservationTask.ColumnReservationStatus')}</div>
          <Select
            mode="multiple"
            showSearch={false}
            placeholder={t('Please select')}
            value={draft.reservationStatuses}
            onChange={value => changeDraft({ reservationStatuses: toNumbers(value).filter(isReservationTaskReservationStatus) })}
            customStyle={selectStyle}
          >
            {RESERVATION_TASK_RESERVATION_STATUSES.map(status => (
              <Option key={status} value={status}>
                {t(reservationTaskReservationStatusLabelKeys[status])}
              </Option>
            ))}
          </Select>
        </div>
        <div css={selectFieldStyle}>
          <div css={fieldTitleStyle}>{t('reservationTask.ColumnHandlingStatus')}</div>
          <Select
            mode="multiple"
            showSearch={false}
            placeholder={t('Please select')}
            value={draft.handlingStatuses}
            onChange={value => changeDraft({ handlingStatuses: toNumbers(value).filter(isReservationTaskHandlingStatus) })}
            customStyle={selectStyle}
          >
            {RESERVATION_TASK_HANDLING_STATUSES.map(status => (
              <Option key={status} value={status}>
                {t(reservationTaskHandlingStatusLabelKeys[status])}
              </Option>
            ))}
          </Select>
        </div>
        <div css={selectFieldStyle}>
          <div css={fieldTitleStyle}>{t('reservationTask.ColumnStaff')}</div>
          <Select
            mode="multiple"
            showSearch={false}
            placeholder={t('Please select')}
            value={draft.staffIds}
            onChange={value => changeDraft({ staffIds: toNumbers(value) })}
            customStyle={selectStyle}
          >
            {staffOptions.map(staff => (
              <Option key={staff.id} value={staff.id}>
                {staff.name}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div css={fieldRowStyle}>
        <div css={updatedAtFieldStyle}>
          <div css={fieldTitleStyle}>{t('reservationTask.ColumnUpdatedAt')}</div>
          <div css={dateTimeRangeStyle}>
            {/* showClearDate を付けないと、選んだ日付だけを消す手段が無い（DateField は showClearDate のときだけ削除アイコンを出す） */}
            <DateField
              id="reservation-task-updated-at-from"
              showClearDate
              value={updatedAtFrom.date || null}
              placeholder={t('reservationTask.DatePlaceholder')}
              onChange={date =>
                changeDraft({ reservationUpdatedAtFrom: joinDateTime(date ? date.format('YYYY-MM-DD') : '', updatedAtFrom.time) })
              }
            />
            <TimeSelect
              value={updatedAtFrom.time}
              disabled={!updatedAtFrom.date}
              onChange={time => changeDraft({ reservationUpdatedAtFrom: joinDateTime(updatedAtFrom.date, time) })}
            />
            <span css={tildeStyle}>~</span>
            <DateField
              id="reservation-task-updated-at-to"
              showClearDate
              value={updatedAtTo.date || null}
              placeholder={t('reservationTask.DatePlaceholder')}
              onChange={date =>
                changeDraft({ reservationUpdatedAtTo: joinDateTime(date ? date.format('YYYY-MM-DD') : '', updatedAtTo.time) })
              }
            />
            <TimeSelect
              value={updatedAtTo.time}
              disabled={!updatedAtTo.date}
              onChange={time => changeDraft({ reservationUpdatedAtTo: joinDateTime(updatedAtTo.date, time) })}
            />
          </div>
        </div>
        <div>
          <div css={fieldTitleStyle}>{t('reservationTask.FilterCheckinDate')}</div>
          <DateRangeField
            beginDate={draft.checkinDateFrom || undefined}
            endDate={draft.checkinDateTo || undefined}
            changeRangeDate={(from: string, to: string) => changeDraft({ checkinDateFrom: from ?? '', checkinDateTo: to ?? '' })}
          />
        </div>
      </div>
      {hasRangeError && <div css={errorStyle}>{t('reservationTask.UpdatedAtRangeError')}</div>}
      <div css={footerStyle}>
        <Button
          buttonType={ButtonType.Secondary}
          width={92}
          height={32}
          fontSize={12}
          onClick={() => changeDraft(INITIAL_RESERVATION_TASK_QUERY.filter)}
        >
          {t('Clear')}
        </Button>
        <Button buttonType={ButtonType.Primary} width={93} height={32} fontSize={12} marginLeft={16} onClick={apply}>
          {t('Apply')}
        </Button>
      </div>
    </div>
  )
}

const FIELD_WIDTH = 320
const FIELD_GAP = 24

// 既存タブの詳細検索パネル（_reservation/detailed-reserch-panel.tsx）と同じく、一覧の上に重ねて出す。
// 位置は固定値ではなく、タブの中身（position: relative）の帯（高さ 66）の下に合わせる。
// 幅は既存タブのパネル（ビューポート基準の 91.6%）に揃える
const panelStyle = css({
  position: 'absolute',
  top: 66,
  left: 0,
  width: '91.6vw',
  zIndex: 50,
  padding: '24px 29px 0',
  background: '#FFFFFF',
  borderRadius: 5,
  boxShadow: '0px 3px 10px #0000003d',
})

const fieldRowStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: FIELD_GAP,
  marginBottom: 16,
})

const selectFieldStyle = css({
  width: FIELD_WIDTH,
})

// 次の「チェックイン日」を1行目の「対応スタッフ」と同じ列に揃えるため、1行目の2列分の幅を取る
const updatedAtFieldStyle = css({
  width: FIELD_WIDTH * 2 + FIELD_GAP,
})

const fieldTitleStyle = css({
  marginBottom: 8,
  fontSize: 12,
  fontWeight: 'bold',
  color: '#676767',
})

const selectStyle = css({
  paddingBottom: 0,
})

const timeSelectStyle = css({
  width: 96,
  paddingBottom: 0,
})

const dateTimeRangeStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const tildeStyle = css({
  margin: '0 4px',
})

const errorStyle = css({
  marginBottom: 8,
  fontSize: 12,
  color: '#F44D10',
})

const footerStyle = css({
  display: 'flex',
  padding: '16px 0',
  borderTop: '1px solid #F2F2F2',
})
